import { useEffect, useRef, useState } from "react"
import { Outlet } from "react-router-dom"
import { AppSidebar } from "@/components/app-sidebar"
import { SiteHeader } from "@/components/site-header"
import { DemoBanner } from "@/components/demo-banner"
import { GlobalSearch, useGlobalSearchHotkey } from "@/components/global-search/global-search"
import { MobileBottomNav } from "@/components/mobile-bottom-nav"
import { SidebarInset, SidebarProvider } from "@/components/ui/sidebar"
import { TooltipProvider } from "@/components/ui/tooltip"
import { ProductTourProvider } from "@/features/product-tour/product-tour"

// Hover-trigger zone width when sidebar is collapsed (px)
const SIDEBAR_WIDTH_PX = 20
const SIDEBAR_PINNED_STORAGE_KEY = "chrona:ui:sidebar:pinned"

function readSidebarPinnedPreference() {
  if (typeof window === "undefined") return true
  const raw = window.localStorage.getItem(SIDEBAR_PINNED_STORAGE_KEY)
  if (raw == null) return true
  return raw === "1"
}

export function AppLayout() {
  const [pinned, setPinned] = useState(readSidebarPinnedPreference)
  const [searchOpen, setSearchOpen] = useState(false)
  const [hovering, setHovering] = useState(false)
  const hoveringRef = useRef(false)
  const leaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    window.localStorage.setItem(SIDEBAR_PINNED_STORAGE_KEY, pinned ? "1" : "0")
  }, [pinned])

  useGlobalSearchHotkey(searchOpen, setSearchOpen)

  useEffect(() => {
    if (pinned) {
      hoveringRef.current = false
      return
    }

    function onMouseMove(e: MouseEvent) {
      // When hovering: keep open while mouse is within full sidebar width (16rem = 256px)
      // When collapsed: trigger zone is narrow (SIDEBAR_WIDTH_PX)
      const threshold = hoveringRef.current ? 256 : SIDEBAR_WIDTH_PX
      if (e.clientX <= threshold) {
        if (leaveTimer.current) { clearTimeout(leaveTimer.current); leaveTimer.current = null }
        hoveringRef.current = true
        setHovering(true)
      } else {
        if (!leaveTimer.current) {
          leaveTimer.current = setTimeout(() => {
            hoveringRef.current = false
            setHovering(false)
            leaveTimer.current = null
          }, 200)
        }
      }
    }

    window.addEventListener("mousemove", onMouseMove)
    return () => {
      window.removeEventListener("mousemove", onMouseMove)
      if (leaveTimer.current) clearTimeout(leaveTimer.current)
    }
  }, [pinned])

  return (
    <TooltipProvider>
      <div className="[--header-height:43px] h-svh overflow-hidden">
        <SidebarProvider
          open={pinned}
          onOpenChange={(v) => { setPinned(v); if (!v) setHovering(false) }}
        >
          <ProductTourProvider>
            <div
              data-sidebar-hovered={hovering ? "" : undefined}
              data-sidebar-float={!pinned ? "" : undefined}
            >
              <AppSidebar />
            </div>
            <SidebarInset className="overscroll-none h-svh overflow-hidden min-w-0 md:peer-data-[variant=inset]:m-0 md:peer-data-[variant=inset]:rounded-none md:peer-data-[variant=inset]:shadow-none">
              <DemoBanner />
              <SiteHeader onOpenSearch={() => setSearchOpen(true)} />
              <div className="min-h-0 min-w-0 flex-1 overflow-hidden flex flex-col pb-[calc(4rem+env(safe-area-inset-bottom))] md:pb-0">
                <Outlet />
              </div>
            </SidebarInset>
            <MobileBottomNav onOpenSearch={() => setSearchOpen(true)} />
            <GlobalSearch open={searchOpen} onOpenChange={setSearchOpen} />
          </ProductTourProvider>
        </SidebarProvider>
      </div>
    </TooltipProvider>
  )
}
